import { useRef, useState } from 'react';
import { Upload, X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/client';

// Backend returns a path like `/api/uploads/products/<key>.webp`.
// Convert to an absolute URL so the stored value works regardless
// of which origin (admin / public storefront / iframe preview)
// renders the <img>.
function absoluteImageUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('data:')) return url;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return base.replace(/\/$/, '') + (url.startsWith('/') ? url : `/${url}`);
}

export function SingleImageUpload({ label, hint, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const { data } = await adminApi.uploadImage(file);
      onChange(absoluteImageUrl(data.url));
      toast.success(`Geüpload (${data.width}×${data.height}, ${(data.size / 1024).toFixed(0)} KB)`);
    } catch (err) {
      // Surface the backend `detail` (size/type/corrupt errors)
      // instead of the historic generic "Upload mislukt".
      const detail = err.response?.data?.detail
        || err.response?.statusText
        || err.message
        || 'Upload mislukt';
      setError(detail);
      toast.error(detail);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label}{hint && <span className="ml-1.5 text-xs text-muted-foreground font-normal">{hint}</span>}
      </label>
      <div className="flex gap-3 items-start">
        {value ? (
          <div className="relative shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-border bg-muted">
            <img src={value} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
            <button type="button" onClick={() => onChange('')}
              data-testid={`image-clear-${label}`}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="shrink-0 w-20 h-20 rounded-xl border-2 border-dashed border-border bg-muted/50 flex items-center justify-center text-muted-foreground">
            <Upload className="w-5 h-5" />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid={`image-upload-btn-${label}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-card text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50">
            <Upload className="w-3.5 h-3.5" />
            {uploading ? 'Uploaden...' : 'Bestand kiezen'}
          </button>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
            data-testid={`image-input-${label}`}
            className="hidden" onChange={handleFile} />
          <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="of plak een URL..."
            data-testid={`image-url-${label}`}
            className="w-full px-3 py-2 rounded-lg border border-input bg-card text-xs focus:outline-none focus:ring-2 focus:ring-ring" />
          {error && (
            <p data-testid={`image-error-${label}`} className="text-xs text-rose-700">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function GalleryImageUpload({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const { data } = await adminApi.uploadImage(file);
        newUrls.push(absoluteImageUrl(data.url));
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Upload failed';
        toast.error(`Upload failed: ${file.name}`, { description: detail });
      }
    }
    if (newUrls.length) onChange([...images, ...newUrls]);
    setUploading(false);
    e.target.value = '';
  };

  const addUrl = () => {
    const url = window.prompt('Afbeeldings-URL:');
    if (url?.trim()) onChange([...images, url.trim()]);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">Extra afbeeldingen</label>
      {images.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-3">
          {images.map((url, idx) => (
            <div key={`img-${idx}-${url.slice(-12)}`} className="relative w-16 h-16 rounded-lg overflow-hidden border border-border bg-muted group">
              <img src={url} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
              <button type="button" onClick={() => onChange(images.filter((_, i) => i !== idx))}
                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Trash2 className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploaden...' : 'Bestanden kiezen'}
        </button>
        <button type="button" onClick={addUrl}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card text-xs font-medium hover:bg-muted transition-colors">
          <Plus className="w-3.5 h-3.5" /> URL toevoegen
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleFiles} />
      </div>
    </div>
  );
}
